import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { getEventInstanceHistory, type EventInstanceHistoryPage, type EventInstanceHistoryRow } from 'api/event_instances/endpoints/getEventInstanceHistory';
import type { FilterConfig, ListColumnConfig } from 'api/shared/listConfig';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { flattenQuery } from 'helpers/flattenQuery';
import { toDraft } from './toDraft';

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface EventInstanceHistoryState {
  // One writer, one shape: this list is read-only, so nothing looser than the
  // boundary-validated row ever reaches it.
  instances: EventInstanceHistoryRow[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: ListColumnConfig[];
  filtersConfig: FilterConfig[];
  loading: {
    fetchEventInstanceHistory: boolean;
  };
}

const initialState: EventInstanceHistoryState = {
  instances: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 100,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchEventInstanceHistory: false,
  },
};

export const fetchEventInstanceHistory = createAsyncThunk<EventInstanceHistoryPage, Record<string, unknown>, { rejectValue: string }>(
  'eventInstanceHistory/fetch',
  async (params, { rejectWithValue }) => {
    try {
      return await getEventInstanceHistory(flattenQuery(params));
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch event history'));
    }
  }
);

const eventInstanceHistorySlice = createSlice({
  name: 'eventInstanceHistory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventInstanceHistory.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchEventInstanceHistory = true;
      })
      .addCase(fetchEventInstanceHistory.fulfilled, (state, action) => {
        const { instances, config, filtersConfig, pagination } = action.payload;

        state.status = 'succeeded';
        // toDraft: the validated wire row is deeply readonly (including
        // `actors`), which immer's Draft mapping rejects. Nothing mutates these
        // rows -- the list is read-only -- so it asserts what is already true.
        state.instances = toDraft([...instances]);
        state.config = toDraft([...config]);
        state.filtersConfig = toDraft([...filtersConfig]);

        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = pagination.total_pages;
        state.pagination.pageSize = pagination.limit;
        state.pagination.currentPage = pagination.current_page > pagination.total_pages ? 1 : pagination.current_page;

        state.loading.fetchEventInstanceHistory = false;
      })
      .addCase(fetchEventInstanceHistory.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.fetchEventInstanceHistory = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch event history';
      });
  },
});

export default eventInstanceHistorySlice.reducer;
